import { Box, Card, CardContent, CircularProgress, Divider, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { TrackChanges, Edit, Check, Close } from "@mui/icons-material";
import { useState, useEffect } from "react";
import moment from "moment";
import { useGetStudentsQuery } from "../../services/students.service";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { useGetTasksQuery } from "../../services/tasks.service";
import { useGetSettingQuery, useUpsertSettingMutation } from "../../services/settings.service";
import { useAppTheme } from "../../context/ThemeContext";

const SETTING_KEY = "monthly-targets";
const DEFAULTS = { students: 10, submissions: 10, tasks: 20 };

type Targets = typeof DEFAULTS;

function ProgressBar({ value, target, color }: { value: number; target: number; color: string }) {
  const pct = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  const met = target > 0 && value >= target;
  return (
    <Box sx={{ height: 8, borderRadius: 999, bgcolor: "#F1F5F9", overflow: "hidden" }}>
      <Box
        sx={{
          height: "100%",
          width: `${pct}%`,
          bgcolor: met ? "#059669" : color,
          borderRadius: 999,
          transition: "width 0.5s ease",
        }}
      />
    </Box>
  );
}

function MonthlyTargetsWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { data: submissions } = useGetSubmissionsQuery("Submissions");
  const { data: tasks } = useGetTasksQuery("Tasks");
  const { isNewTheme, accentColor } = useAppTheme();

  const { data: setting, isLoading } = useGetSettingQuery(SETTING_KEY);
  const [upsert, { isLoading: isSaving }] = useUpsertSettingMutation();

  const savedTargets: Targets = setting?.value ?? DEFAULTS;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Targets>(savedTargets);

  // Sync draft when remote value loads
  useEffect(() => {
    if (setting?.value) setDraft(setting.value);
  }, [setting]);

  const startOfMonth = moment().startOf("month").toDate();

  const newStudents = (students ?? []).filter(
    (s) => s.createdAt && new Date(s.createdAt as any) >= startOfMonth
  ).length;

  const newSubmissions = (submissions ?? []).filter(
    (s) => s.createdAt && new Date(s.createdAt as any) >= startOfMonth
  ).length;

  const completedTasks = (tasks ?? []).filter(
    (t) => t.status === "Completed" && (t as any).updatedAt && new Date((t as any).updatedAt) >= startOfMonth
  ).length;

  const metrics = [
    { label: "New Students", value: newStudents, target: savedTargets.students, color: accentColor, draftKey: "students" as keyof Targets },
    { label: "Submissions", value: newSubmissions, target: savedTargets.submissions, color: "#8B5CF6", draftKey: "submissions" as keyof Targets },
    { label: "Tasks Done", value: completedTasks, target: savedTargets.tasks, color: "#F59E0B", draftKey: "tasks" as keyof Targets },
  ];

  const handleSave = async () => {
    const next: Targets = {
      students: Math.max(1, draft.students),
      submissions: Math.max(1, draft.submissions),
      tasks: Math.max(1, draft.tasks),
    };
    await upsert({ key: SETTING_KEY, value: next });
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(savedTargets);
    setEditing(false);
  };

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : { width: "100%", borderRadius: "10px", boxShadow: "0px 0px 55px 5px rgba(0,0,0,0.05)" };

  return (
    <Card sx={cardSx}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `${accentColor}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TrackChanges sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Monthly Targets
            </Typography>
            {isNewTheme && <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {moment().format("MMMM YYYY")}
          </Typography>
          {isLoading ? (
            <CircularProgress size={14} sx={{ ml: 0.5 }} />
          ) : editing ? (
            <>
              <Tooltip title="Save" arrow>
                <span>
                  <IconButton size="small" onClick={handleSave} disabled={isSaving} sx={{ color: "#059669" }}>
                    {isSaving ? <CircularProgress size={14} /> : <Check sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel" arrow>
                <IconButton size="small" onClick={handleCancel} sx={{ color: "#94A3B8" }}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Edit targets" arrow>
              <IconButton size="small" onClick={() => { setDraft(savedTargets); setEditing(true); }} sx={{ color: "#94A3B8", "&:hover": { color: accentColor } }}>
                <Edit sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        <Stack spacing={2}>
          {metrics.map((m) => {
            const pct = m.target > 0 ? Math.min(Math.round((m.value / m.target) * 100), 100) : 0;
            const met = m.value >= m.target;

            return (
              <Box key={m.label}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: "0.8rem" }}>
                    {m.label}
                  </Typography>
                  {editing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={draft[m.draftKey]}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [m.draftKey]: Number(e.target.value) }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.disabled">target</Typography></InputAdornment>,
                        sx: { fontSize: "0.75rem", height: 28 },
                      }}
                      sx={{ width: 110 }}
                    />
                  ) : (
                    <Stack direction="row" alignItems="baseline" spacing={0.3}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: met ? "#059669" : m.color }}>
                        {m.value}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">/ {m.target}</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ color: met ? "#059669" : m.color, fontSize: "0.65rem" }}>
                        {pct}%
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <ProgressBar value={m.value} target={m.target} color={m.color} />
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default MonthlyTargetsWidget;
